import { useState, ReactNode, Children } from 'react'
import { Carousel as CarouselBase } from 'react-responsive-carousel'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Carousel = CarouselBase as React.ComponentType<any>
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import { ChevronLeft, ChevronRight } from 'react-feather'
import useResolution from '@hooks/useResolution'

export type CyclicCarouselProps = {
  children: ReactNode
  className?: string
  interval?: number
  onSlideChange?: (index: number) => void
}

export const CyclicCarousel = ({
  children,
  className = '',
  interval = 5000,
  onSlideChange,
}: CyclicCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(1)
  const {
    sizes: { md, sm },
  } = useResolution()

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index)
    onSlideChange?.(index)
  }

  const carouselClass = `cyclic-carousel ${className}`.trim()

  const renderCustomArrow = (
    clickHandler: () => void,
    hasPrev: boolean,
    label: string
  ) => {
    const isPrev = label.includes('previous')
    if (!hasPrev) return null

    return (
      <button
        aria-label={label}
        className={`bg-orange absolute top-1/2 transform -translate-y-1/2 z-20 text-white rounded-md p-2 flex items-center justify-center shadow-lg border-none focus:outline-none delay-150 bg-opacity-50 transition-colors hover:bg-opacity-100 ${
          isPrev ? 'left-0' : 'right-0'
        }`}
        type="button"
        onClick={clickHandler}
      >
        {isPrev ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
      </button>
    )
  }

  const renderCustomDots = (
    clickHandler: (e: React.MouseEvent | React.KeyboardEvent) => void,
    isSelected: boolean,
    _index: number,
    label: string
  ) => {
    return (
      <button
        aria-label={label}
        className={`rounded-full transition-all duration-300 focus:outline-none ${
          isSelected
            ? 'bg-orange w-12 h-2 p-0'
            : 'w-6 h-2 bg-grey-500 border-none cursor-pointer'
        }`}
        onClick={clickHandler}
        style={{
          boxShadow: isSelected ? '0px 3px 4px 1px #ff5a43aa' : '',
        }}
      />
    )
  }

  return (
    <>
      <Carousel
        selectedItem={currentSlide}
        onChange={handleSlideChange}
        showArrows={true}
        showStatus={false}
        showThumbs={false}
        infiniteLoop={true}
        autoPlay={true}
        interval={interval}
        stopOnHover={true}
        swipeable={true}
        emulateTouch={true}
        centerMode={!md && !sm}
        centerSlidePercentage={33.33}
        renderArrowPrev={renderCustomArrow}
        renderArrowNext={renderCustomArrow}
        renderIndicator={renderCustomDots}
        className={carouselClass}
      >
        {Children.toArray(children)}
      </Carousel>

      <style jsx global>{`
        .cyclic-carousel .carousel-root {
          overflow: visible;
          position: relative;
        }

        .cyclic-carousel .carousel .slider-wrapper {
          overflow: visible;
          padding-bottom: 3rem;
        }

        .cyclic-carousel .carousel .slider {
          overflow: visible;
          transition: all 700ms cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .cyclic-carousel .carousel .slider.animated {
          transition: all 700ms cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .cyclic-carousel .control-dots {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          margin: 0;
          padding: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
        }

        .cyclic-carousel .slide {
          opacity: 0.7;
          transform: scale(0.75);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
          border-radius: 0.625rem;
          overflow: hidden;
          height: 360px;
          width: 470px;
          max-height: 360px;
          max-width: 470px;
          margin: 0 auto;
        }

        .cyclic-carousel .slide.selected {
          opacity: 1;
          transform: scale(1);
        }

        @media (max-width: 767px) {
          .cyclic-carousel .slide {
            opacity: 1;
            transform: scale(1);
            width: 100%;
            max-width: 470px;
          }
        }
      `}</style>
    </>
  )
}
